import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { COLLECTIONS, ROLES, isGeneralUnitId } from '../platform/constants';
import { effectiveRole } from '../platform/membershipPermissions';

function mapUnitDoc(docSnap) {
  return { id: docSnap.id, ...docSnap.data() };
}

/** All unit docs for the active tenant (server-scoped for Firestore rules). */
export async function fetchTenantUnits(activeTenantId) {
  if (!activeTenantId) {
    return [];
  }

  const snapshot = await getDocs(
    query(
      collection(db, COLLECTIONS.UNITS),
      where('tenantId', '==', activeTenantId),
    ),
  );
  return snapshot.docs.map(mapUnitDoc);
}

/**
 * Units visible to the current user within the active tenant.
 */
export async function fetchUnitsForTenant(activeTenantId, { user, memberships = [] } = {}) {
  const tenantUnits = await fetchTenantUnits(activeTenantId);

  if (!user) {
    return tenantUnits;
  }

  const role = effectiveRole(memberships, activeTenantId, user);
  if (role === ROLES.ADMIN || role === ROLES.LOCAL_ADMIN) {
    return tenantUnits;
  }

  return tenantUnits.filter(
    (unit) => unit.id === user.unit || isGeneralUnitId(unit.id, activeTenantId),
  );
}

/** Chat room list — same tenant scope, role-based visibility. */
export async function fetchUnitsForChat(activeTenantId, { role, userUnit }) {
  const tenantUnits = await fetchTenantUnits(activeTenantId);

  if (role === ROLES.ADMIN || role === ROLES.LOCAL_ADMIN) {
    return tenantUnits;
  }

  if (!userUnit) {
    return [];
  }

  const match = tenantUnits.find((unit) => unit.id === userUnit);
  return match ? [match] : [];
}

/** All units for the active tenant (admin create forms). */
export async function fetchAllUnits(activeTenantId) {
  return fetchTenantUnits(activeTenantId);
}

/** Map of unit id -> unitName for the active tenant. */
export async function fetchUnitNameMap(activeTenantId) {
  const units = await fetchTenantUnits(activeTenantId);
  return units.reduce((acc, unit) => {
    acc[unit.id] = unit.unitName;
    return acc;
  }, {});
}

/** @deprecated Prefer fetchUnitsForTenant(activeTenantId, { user, memberships }). */
export async function fetchUnitsForUser(user, activeTenantId, memberships = []) {
  return fetchUnitsForTenant(activeTenantId, { user, memberships });
}
